import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import './PricePricingPage.css';
import PageHeader from '../../../shared/PageHeader';
import BrowseNav, { useBrowseIds } from '../../../shared/BrowseNav';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import PriceCalculator from '../../../shared/PriceCalculator';
import SaveButton from '../../../shared/SaveButton';
import CancelButton from '../../../shared/CancelButton';
import { SystemPill } from '../../../shared/Pill';
import { StatusPill } from './PriceTable';
import DateInput from '../../../shared/DateInput';
import { formatDate } from '../../../shared/tenantClock';
import NumberInput from '../../../shared/NumberInput';
import { formatMoney, formatNumber, formatPercent } from '../../../shared/numberFormat';
import TaxBasisLine from '../../../shared/TaxBasisLine';
import { askConfirm } from '../../../shared/AppDialogs';

// Same formatting behavior as Retail Prices' InlineMoneyEdit — the shared
// NumberInput holds the draft and this commits on blur, rather than being
// driven by parent-held per-keystroke state.
function InlineMoneyEdit({ price, onCommit, onFocus }) {
  return (
    <NumberInput
      value={price}
      decimals={2}
      min="0"
      onFocus={() => onFocus?.()}
      onBlur={(e) => onCommit(e.target.value)}
      style={{ width: 90, textAlign: 'right' }}
    />
  );
}

// Floating callout for an inline-edit error — positioned absolutely (out of
// document flow) so a long validation message never grows the row's height
// or knocks neighbouring rows' fields out of column alignment, unlike a
// plain in-flow <div> would. Requires the parent <td> to be position:
// relative, since this anchors to it. Opens upward (bottom: '100%') rather
// than down — the table's wrapping div clips to its rounded corners via
// overflow: hidden, and a callout opening below the last (or only) row has
// no clearance before that clip boundary, silently disappearing; opening
// upward always has a header/previous row above providing room.
function RowErrorCallout({ children }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: '100%',
        left: 0,
        marginBottom: 4,
        width: 180,
        textAlign: 'left',
        background: '#fff',
        border: '1px solid #fecaca',
        borderRadius: 4,
        padding: '4px 8px',
        fontSize: 11,
        color: '#b91c1c',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        zIndex: 5,
      }}
    >
      {children}
    </div>
  );
}

function formatCustomerScope(adjustment) {
  if (adjustment.customer) return adjustment.customer.name;
  if (adjustment.customer_price_group) return `Group: ${adjustment.customer_price_group.name}`;
  return 'All Customers';
}

// Label/value split for the Pricing page's scope rows — each row bolds its
// label (Group/Customer/Product/UoM), not its value, per user feedback that
// the reverse (bold value) buried the UoM.
function customerScopeRow(adjustment) {
  if (adjustment.customer) return { label: 'Customer', value: adjustment.customer.name };
  if (adjustment.customer_price_group) return { label: 'Group', value: adjustment.customer_price_group.name };
  return { label: null, value: 'All Customers' };
}

// A specific Product splits into its own SKU and Product(name) rows — Group
// and All-Products scopes have no SKU, so they stay a single row.
function productScopeRows(adjustment) {
  if (adjustment.product) {
    return [
      { label: 'SKU', value: adjustment.product.sku },
      { label: 'Product', value: adjustment.product.name },
    ];
  }
  if (adjustment.product_price_group) return [{ label: 'Group', value: adjustment.product_price_group.name }];
  return [{ label: null, value: 'All Products' }];
}

// A specific UoM's name (e.g. "Pack") needs the "UoM:" label to make sense
// standing alone — "(All UoMs)" already says what it means without it,
// same self-explanatory-value rule the Customer/Product rows above follow.
// Every Price now specifies a direct UoM, so a blank one only ever shows up
// on a legacy row saved before that rule existed.
function uomScopeRow(adjustment) {
  if (adjustment.uom) return { label: 'UoM', value: adjustment.uom.name };
  return { label: null, value: '(All UoMs)' };
}

// 3dp variant for the read-only Price Breaks columns derived by division
// (inc-GST and the Equiv Base UoM columns) — 2dp rounds away exactly the
// small discrepancies those columns exist to surface.
function formatMoney3(value) {
  return formatMoney(value, { decimals: 3 }) || '—';
}

// Mirrors retailPriceService.js's uomRetailFromBase exactly: the Base Price
// inc GST x the UoM's quantity, to the whole cent — the figure every
// Pricelist, quote and order uses for that UoM's Retail Price. Returns
// { inc, ex } (ex at 4dp). taxRate is a fraction (0.1), as held on this page.
function calculatedUomRetailPrice(basePriceInc, conversionRate, taxRate) {
  const rate = Number(conversionRate);
  if (basePriceInc === null || basePriceInc === undefined || !(rate > 0)) return null;
  const inc = Math.round(Number((Number(basePriceInc) * rate * 100).toFixed(6))) / 100;
  return { inc, ex: Math.round((inc / (1 + (taxRate || 0))) * 10000) / 10000 };
}

// Retail's Base Price inc GST as stored (price_inc), or — for a break saved
// before price_inc existed — recovered from its ex price, snapped to the
// whole cent it was keyed as (same as retailPriceService.js's incFromEx).
function retailBreakInc(brk, taxRate) {
  if (!brk) return null;
  if (brk.price_inc !== null && brk.price_inc !== undefined) return Number(brk.price_inc);
  const cents = Number(brk.price) * (1 + (taxRate || 0)) * 100;
  return Math.abs(cents - Math.round(cents)) < 0.01 ? Math.round(cents) / 100 : Math.round(cents * 100) / 10000;
}

function formatMoney2(value) {
  return formatMoney(value) || '—';
}

// 4dp variant for the Add a Price Break helper specifically — it's a
// conversion preview (GST and/or UoM), same "derived by division/
// multiplication" class of problem formatMoney3 exists for, just needing
// one more digit here since it's what you'd type into Price on the
// opposite side, not just a read-only display.
function formatMoney4(value) {
  return formatMoney(value, { decimals: 4 }) || '—';
}

// The UoM's tenant-entered Plural Name ("Pieces") when the quantity isn't 1,
// only for display; never touches the stored UoM name itself. With no Plural
// Name set, falls back to a best-effort guess ("Pack" -> "Packs", "Box" ->
// "Boxes") that can't be perfect for every word — enough to stop "10 Pack"
// reading like a bundle-size product name instead of a quantity of Packs.
// A name already ending in a single "s" ("Pieces", "Units") is left as typed.
function pluralizeUomName(uom, qty) {
  const name = uom?.name;
  if (!name || Number(qty) === 1) return name;
  if (uom.plural_name) return uom.plural_name;
  if (/[^s]s$/i.test(name)) return name;
  return /[sxz]$|[cs]h$/i.test(name) ? `${name}es` : `${name}s`;
}

// Read-only, derived from a Base-UoM quantity × conversion rate — never
// itself editable. Used only for the "typing in this Price's own UoM"
// entry mode, to echo what that's worth in Base terms before it's saved.
function formatBaseQty(rawQty, conversionRate, baseUom) {
  if (rawQty === null || rawQty === undefined || rawQty === '' || !conversionRate) return '—';
  const qty = Number(rawQty) * conversionRate;
  const formatted = formatNumber(qty, { decimals: 0 });
  // "×" rather than a bare space — "10 Pack" reads as a bundle-size name
  // (like a product variant), not a quantity; "10 × Packs" can't be misread.
  return baseUom?.name ? `${formatted} × ${pluralizeUomName(baseUom, qty)}` : formatted;
}

// The inverse: a Base-UoM quantity (what Min Qty is actually stored as —
// see the Pricing domain's Min Qty semantics change) divided back into
// this Price's own selling UoM, e.g. 50 PCS -> 5 Packs. Ceiled since a
// partial Pack can't reach a threshold. Blank when there's nothing to
// derive from.
function formatSellingQty(baseQty, conversionRate, sellingUom) {
  if (baseQty === null || baseQty === undefined || baseQty === '' || !conversionRate) return '—';
  const qty = Math.ceil(Number(baseQty) / conversionRate);
  const formatted = formatNumber(qty, { decimals: 0 });
  return sellingUom?.name ? `${formatted} × ${pluralizeUomName(sellingUom, qty)}` : formatted;
}

// One calendar day after a 'YYYY-MM-DD' string, computed in UTC so it
// can't drift a day off around a local-timezone DST boundary — same
// approach the old per-Product Retail schedule form used before this
// screen absorbed it.
function nextDayString(dateStr) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

// The current "selected" period plus every sibling sharing its exact Scope
// (Product/Customer Scope + UoM), oldest first — the root `adjustment`
// fetched by route id is just one entry among them now, not privileged
// over its siblings. Each entry already carries its own Breaks and linked
// Rule (backend's loadScheduleForScope, widened 2026-09-14) so selecting a
// different row is a pure client-side swap, never a fetch.
function buildAllPeriods(adjustment) {
  return [
    {
      id: adjustment.id,
      start_date: adjustment.start_date,
      end_date: adjustment.end_date,
      status: adjustment.status,
      type: adjustment.type,
      rule_id: adjustment.rule_id,
      rule: adjustment.rule,
      breaks: adjustment.breaks,
      needs_price: adjustment.needs_price,
    },
    ...(adjustment.schedule || []),
  ].sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
}

// Default selection: whichever period covers today, else whichever period
// this page was actually navigated to (the route's own id), else just the
// first one chronologically. Only consulted when there's no existing
// selection to keep, or that selection just stopped existing (its period
// was deleted) — see the effect that calls this.
function defaultPeriodId(periods, rootId) {
  const current = periods.find((p) => p.status === 'current');
  if (current) return current.id;
  const root = periods.find((p) => p.id === rootId);
  if (root) return root.id;
  return periods[0]?.id ?? null;
}

// Shown whenever the selected period is still genuinely unconfirmed — no
// Price Break yet, no sibling period — matching priceController.js's
// assertMethodStillChangeable exactly (a Rule choice gets the same window
// as Fixed Amount now, not an instant lock: see that function's own
// comment for why). Lives directly in the Schedule table's own Method
// column, replacing that cell's plain text for whichever row is still
// unconfirmed — same "commit on change, no separate Save" pattern as the
// table's own inline date/price edits, rather than a separate section
// with its own button.
function InlineMethodSelect({ period, priceRules, onSetMethod, onChoiceChange, saving }) {
  const [choice, setChoice] = useState(period.rule_id ? 'rule' : 'fixed');
  const [ruleId, setRuleId] = useState(period.rule_id ? String(period.rule_id) : '');

  useEffect(() => {
    setChoice(period.rule_id ? 'rule' : 'fixed');
    setRuleId(period.rule_id ? String(period.rule_id) : '');
    // Deliberately keyed on period.id alone, not period.rule_id — a
    // successful onSetMethod re-fetches data with period.rule_id already
    // matching whatever was just picked, so local state already agrees
    // without this effect needing to re-fire; keying on rule_id too would
    // just re-run this reset redundantly on every save.
    // eslint-disable-next-line
  }, [period.id]);

  // Switching to Fixed commits immediately (nothing further to pick).
  // Switching to Calculation only reveals the Rule dropdown — it commits
  // once an actual Rule is chosen, since rule_id is required and "picked
  // Calculation but no Rule yet" isn't a state the backend accepts.
  // onChoiceChange fires regardless, though — the parent uses it to hide
  // the Price Breaks card the instant Calculation is picked, not just once
  // a Rule is actually chosen and saved (Breaks would otherwise sit there
  // implying Fixed is still live, in the same in-between window the
  // standalone Method card used to create).
  const handleTopChange = (value) => {
    setChoice(value);
    onChoiceChange?.(period.id, value);
    if (value === 'fixed' && period.rule_id) onSetMethod(period, 'fixed', '');
  };
  const handleRuleChange = (value) => {
    setRuleId(value);
    if (value) onSetMethod(period, 'rule', value);
  };

  return (
    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
        <input type="radio" checked={choice === 'fixed'} disabled={saving} onChange={() => handleTopChange('fixed')} />
        Fixed Amount
      </label>
      <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
        <input type="radio" checked={choice === 'rule'} disabled={saving} onChange={() => handleTopChange('rule')} />
        Calculation
      </label>
      {choice === 'rule' && (
        <select
          className="styled-input"
          value={ruleId}
          onChange={(e) => handleRuleChange(e.target.value)}
          disabled={saving}
          style={{ fontSize: 12, padding: '2px 4px', width: 130 }}
        >
          <option value="">Select a Rule…</option>
          {priceRules.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      )}
    </div>
  );
}

function formatTierValue(rule, tier) {
  return rule.type === 'percent_off' ? formatPercent(tier.amount) : formatMoney(tier.amount);
}

const ROUNDING_INCREMENT_LABELS = { 1: '1 cent', 5: '5 cents', 10: '10 cents' };
const ROUNDING_DIRECTION_LABELS = { nearest: 'Nearest', up: 'Up', down: 'Down' };

// Mirrors PriceRuleForm.js's own Round To/Round Direction fields — this is
// just the read-only display of the same rounding_increment/rounding_direction
// pair roundToRule() (priceResolution.js) actually applies.
function formatRounding(rule) {
  const increment = Number(rule.rounding_increment) || 1;
  const direction = rule.rounding_direction || 'nearest';
  const incrementLabel = ROUNDING_INCREMENT_LABELS[increment] || `${increment} cents`;
  const directionLabel = ROUNDING_DIRECTION_LABELS[direction] || direction;
  return `${directionLabel} — ${incrementLabel}`;
}

// Read-only counterpart to the Price Breaks card, for a Rule-priced period
// — a Rule's own tiers are managed on its own Price Rules screen, never
// here; this just shows what this period is actually using. A Rule's Min
// Qty is defined once and reused by every Price that references it (see
// vis_prices-and-pricelists.md's Price Rule section) — it deliberately
// carries no UoM of its own, so the same "10% off" Rule can be shared by a
// Pieces-scoped Price and a Pack-scoped Price alike. A tier's Min Qty is
// always stored in the product's Base UoM (Pieces) precisely because of
// that reuse — "10" means the same real quantity regardless which Price or
// UoM matches it — so the extra column here shows what that's worth in
// THIS Price's own selling UoM instead (same showUomPriceHelper/
// conversionRate/formatSellingQty the Price Breaks table already uses, so
// both tables convert identically).
function CalculationCard({ rule, showUomPriceHelper, conversionRate, sellingUom }) {
  const tiers = [...(rule.tiers || [])].sort((a, b) => a.min_quantity - b.min_quantity);
  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>Calculation</h4>
      <div style={{ fontSize: 14, marginBottom: 4 }}>{rule.name}</div>
      <div style={{ fontSize: 12, color: '#8a94a6', marginBottom: tiers.length > 0 ? 10 : 0 }}>Rounding: {formatRounding(rule)}</div>
      {tiers.length > 0 && (
        <table style={{ borderCollapse: 'collapse', fontSize: 13 }}>
          <thead className="list-head">
            <tr>
              <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>Min Qty<br />(Base UoM)</th>
              {showUomPriceHelper && (
                <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600, color: '#888' }}>= {sellingUom?.name}</th>
              )}
              <th style={{ padding: '6px 12px', textAlign: 'right', fontWeight: 600 }}>{rule.type === 'percent_off' ? 'Discount' : 'Amount'}</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier) => (
              <tr key={tier.id} className="list-row">
                <td style={{ padding: '4px 12px', textAlign: 'right' }}>{formatNumber(tier.min_quantity, { maxDecimals: 4 })}</td>
                {showUomPriceHelper && (
                  <td style={{ padding: '4px 12px', textAlign: 'right', color: '#888' }}>
                    {formatSellingQty(tier.min_quantity, conversionRate, sellingUom)}
                  </td>
                )}
                <td style={{ padding: '4px 12px', textAlign: 'right' }}>{formatTierValue(rule, tier)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// The detail half of the Prices list's header/detail split — a dedicated
// page ("Pricing", kebab item of the same name) rather than fields crammed
// into that list's own Add form. Everything that *forks* by pricing method
// lives here: the Rule-vs-Fixed Override choice itself, a Rule reference
// (if chosen), or a Fixed Override's quantity-break schedule (if chosen) —
// plus the Scope's own dated Schedule, managed entirely on this page since
// PriceForm.js's Add is the only place Scope/UoM ever get set (fixed for
// the life of the record after that).
function PricePricingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const browseIds = useBrowseIds();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [adjustment, setAdjustment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // Lets "enter as Inc-GST" convert to the ex-GST value this app actually
  // stores — same tenant default-rate concept as Retail Prices' Add-a-Price
  // form. null disables the Inc-GST option rather than guessing a rate.
  const [defaultTaxRate, setDefaultTaxRate] = useState(null);

  // Which row of the Schedule table is showing in the Breaks/Detail section
  // below it — in-page state, never a route change. Every period's own
  // Breaks data already arrived in the one fetch (buildAllPeriods), so
  // clicking a row is a pure re-render, not a fetch — this is exactly what
  // replaces the old per-period `navigate()` that could leave a period's
  // view blank/unresponsive mid-switch. Resolved to a real id (never left
  // null) by the effect below; null only for the first render or the
  // instant after a fetch before that effect has run.
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  // Schedule a new price period — every Group's Price is a dated history
  // now (not just Retail's), so this same page that shows one period's own
  // Price Breaks also shows every other period sharing its exact Scope,
  // and can open the next one. Reuses the plain addPrice endpoint
  // (POST /api/prices) with this record's own Scope fields — the existing
  // overlap/auto-supersede logic there closes the previous open-ended
  // period automatically, same as it always has for any two Prices sharing
  // a Scope. Method (Fixed Amount vs Calculation) is picked via
  // newPeriodMethod/newPeriodRuleId below, same InlineMethodSelect control
  // the Schedule table's own Method column uses per row while unlocked.
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [newPeriodFrom, setNewPeriodFrom] = useState('');
  const [newPeriodTo, setNewPeriodTo] = useState('');
  const [newPeriodMethod, setNewPeriodMethod] = useState('fixed');
  const [newPeriodRuleId, setNewPeriodRuleId] = useState('');
  const [schedulingSaving, setSchedulingSaving] = useState(false);

  // Every active Price Rule, for both the new-period Method chooser and the
  // Schedule table's own per-row InlineMethodSelect — fetched once, not
  // per-period, since the same tenant-wide list serves both.
  const [priceRules, setPriceRules] = useState([]);
  const [methodSaving, setMethodSaving] = useState(false);
  // The Schedule table's InlineMethodSelect reports every radio flip here,
  // live, before it's ever saved — lets the Price Breaks card below hide
  // the instant Calculation is picked for the selected period, rather than
  // waiting for a Rule to actually be chosen and committed (Breaks
  // defaulting into view for that in-between moment is exactly the "looks
  // like Fixed was already decided" problem the whole inline redesign
  // exists to avoid). Keyed by period id since InlineMethodSelect renders
  // once per unlocked row, not just the selected one.
  const [pendingMethodChoice, setPendingMethodChoice] = useState({ periodId: null, choice: null });

  // "Change" beside the Customer Group in the header — moves the whole
  // Schedule to another group, not just the selected period (see
  // priceController.js's changePriceLineCustomerGroup). Groups load only
  // once the picker is opened.
  const [changingGroup, setChangingGroup] = useState(false);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [newGroupId, setNewGroupId] = useState('');
  const [groupSaving, setGroupSaving] = useState(false);

  const [addQuantity, setAddQuantity] = useState('');
  const [addPrice, setAddPrice] = useState('');
  const [addGstMode, setAddGstMode] = useState('ex');
  // Which UoM the typed Price represents — this Price's own UoM
  // (e.g. Pack, the normal case) or the product's Base UoM, for when the
  // number on hand (a supplier cost, an RRP-derived figure) is actually a
  // per-Base-unit price and needs converting up to a Pack price before it
  // can be saved. Only meaningful once productUoms below tells us this
  // adjustment's UoM isn't already the Base UoM itself.
  const [addPriceUomMode, setAddPriceUomMode] = useState('base');
  // Which UoM the typed Min Qty represents — mirrors addPriceUomMode, since
  // a Price Break's min_quantity is stored in Base UoM (Pieces).
  // 'base' is the default since Base UoM is the reference everything is
  // entered and saved against; only meaningful once showUomPriceHelper is
  // true.
  const [addQtyUomMode, setAddQtyUomMode] = useState('base');
  const [addSaving, setAddSaving] = useState(false);

  // One-shot: which period's Min Qty field to focus once its own row
  // becomes selected — set right after creating a new period (always
  // Fixed Override, always zero Breaks, so the Add-a-Price-Break form is
  // exactly the next thing to type into) and cleared as soon as it fires,
  // so it never re-focuses on an ordinary row click into an empty period.
  const [focusMinQtyPeriodId, setFocusMinQtyPeriodId] = useState(null);
  const minQtyInputRef = useRef(null);
  const effectiveFromInputRef = useRef(null);
  const pendingAddBreakFocus = useRef(false);

  // Same show/hide-behind-a-button pattern as "+ Schedule a new period"
  // above, applied to "Add a Price Break" — an always-open form is a
  // distraction once there's an existing Breaks table to actually read.
  // Defaults per selected period (not per render): open on a fresh,
  // unpriced period (nothing yet to distract from, and it's the one thing
  // to do next), collapsed once it already has Breaks. Only resets on an
  // actual period switch (keyed on selectedPeriodId alone below) — adding
  // a Break within the same period must never auto-collapse the form out
  // from under whoever's mid-way through adding several.
  const [showAddBreakForm, setShowAddBreakForm] = useState(true);

  // Per-product conversion rates — only fetched once the Product Scope
  // resolves to one specific SKU, since a Product Price Group or "All
  // Products" scope has no single conversion rate to convert against.
  const [productUoms, setProductUoms] = useState([]);

  // Inline-edit errors for existing rows, keyed by break id then field
  // ('qty' | 'price') — these edits aren't inside a <form>, so there's no
  // native submit event for the browser to block on a bad value the way the
  // Add-a-Price-Break form gets for free. Shown next to the offending field
  // instead of an alert(), matching the Add form's own inline error style.
  const [rowErrors, setRowErrors] = useState({});

  const setRowError = (brkId, field, message) => {
    setRowErrors((prev) => ({ ...prev, [brkId]: { ...prev[brkId], [field]: message } }));
  };
  const clearRowError = (brkId, field) => {
    setRowErrors((prev) => {
      if (!prev[brkId]?.[field]) return prev;
      const nextRow = { ...prev[brkId] };
      delete nextRow[field];
      const next = { ...prev };
      if (Object.keys(nextRow).length === 0) delete next[brkId];
      else next[brkId] = nextRow;
      return next;
    });
  };

  // BrowseNav can change `id` (moving to a different Price/Scope entirely)
  // without remounting this page, which can fire a new fetch before the
  // previous one's response has landed. Tagging every request and only
  // applying the latest one's result keeps `adjustment` in lockstep with
  // `id` rather than risking a stale, out-of-order response overwriting it.
  const latestRequestId = useRef(0);

  const fetchData = async () => {
    const requestId = ++latestRequestId.current;
    try {
      const res = await axios.get(`/api/prices/${id}`);
      if (latestRequestId.current !== requestId) return;
      setAdjustment(res.data.data);
      setLoadError('');
    } catch (err) {
      if (latestRequestId.current !== requestId) return;
      setLoadError(err.response?.data?.message || 'Could not load Price');
    }
    if (latestRequestId.current === requestId) setLoading(false);
  };

  useEffect(() => {
    setSelectedPeriodId(null);
    fetchData();
    axios.get('/api/tax-codes').then((res) => {
      const defaultCode = (res.data.data || []).find((t) => t.is_default);
      setDefaultTaxRate(defaultCode ? Number(defaultCode.rate) / 100 : null);
    }).catch(() => setDefaultTaxRate(null));
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    axios.get('/api/price-rules', { params: { status: 'active' } })
      .then((res) => setPriceRules(res.data.data || []))
      .catch(() => setPriceRules([]));
  }, []);

  useEffect(() => {
    if (!adjustment?.product_id) { setProductUoms([]); return; }
    axios.get(`/api/products/${adjustment.product_id}/uoms`)
      .then((res) => setProductUoms(res.data.data?.uoms || []))
      .catch(() => setProductUoms([]));
    // eslint-disable-next-line
  }, [adjustment?.product_id]);

  // Resolves which row is selected — keeps whatever's already selected as
  // long as it still exists among the current periods, otherwise falls
  // back to defaultPeriodId (runs on first load, and again if the selected
  // period was just deleted out from under it via setSelectedPeriodId(null)).
  useEffect(() => {
    if (!adjustment) return;
    const periods = buildAllPeriods(adjustment);
    const stillValid = selectedPeriodId != null && periods.some((p) => p.id === selectedPeriodId);
    const effectiveId = stillValid ? selectedPeriodId : defaultPeriodId(periods, adjustment.id);
    if (effectiveId !== selectedPeriodId) setSelectedPeriodId(effectiveId);
    // eslint-disable-next-line
  }, [adjustment, selectedPeriodId]);

  // Re-defaults showAddBreakForm whenever the selection actually moves to
  // a different period — deliberately keyed on selectedPeriodId alone, not
  // on adjustment/breaks, so refetching data for the SAME period (e.g.
  // after adding a Break) never touches this.
  useEffect(() => {
    if (!adjustment || selectedPeriodId == null) return;
    const period = buildAllPeriods(adjustment).find((p) => p.id === selectedPeriodId);
    if (period) setShowAddBreakForm((period.breaks || []).length === 0);
    // A pending, uncommitted Method choice belongs to whichever row it was
    // made on — an actual period switch (not just a refetch of the same
    // one) always clears it, so a stale "Calculation" pick from the
    // previously-selected row can never hide this one's own Breaks card.
    setPendingMethodChoice({ periodId: null, choice: null });
    // eslint-disable-next-line
  }, [selectedPeriodId]);

  // Fires the one-shot focus above once the newly created period's row has
  // actually become the selection (and so its Add-a-Price-Break form is in
  // the DOM) — not on the same render selectedPeriodId is first requested,
  // which is still showing the previous period until the effect above and
  // a fetch settle.
  useEffect(() => {
    if (focusMinQtyPeriodId == null || selectedPeriodId !== focusMinQtyPeriodId) return;
    minQtyInputRef.current?.focus();
    setFocusMinQtyPeriodId(null);
  }, [focusMinQtyPeriodId, selectedPeriodId]);

  // "+ Add a Price Period" mounts the Effective From/To form fresh each
  // time (it's hidden entirely otherwise) — focus Effective From as soon as
  // it exists so typing can start immediately, same as the Price Break
  // min-qty focus above.
  useEffect(() => {
    if (showScheduleForm) effectiveFromInputRef.current?.focus();
  }, [showScheduleForm]);

  // Same idea for "+ Add a Price Break", but only when its button is the
  // thing that opened the form — the effect above already opens it
  // automatically on an empty period's row-select (see focusMinQtyPeriodId's
  // own comment), and that case must NOT steal focus. pendingAddBreakFocus
  // is the one-shot flag that tells the two apart.
  useEffect(() => {
    if (!showAddBreakForm || !pendingAddBreakFocus.current) return;
    minQtyInputRef.current?.focus();
    pendingAddBreakFocus.current = false;
  }, [showAddBreakForm]);

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (loadError) return <div style={{ padding: 24, color: 'red' }}>{loadError}</div>;
  if (!adjustment) return null;

  const allPeriods = buildAllPeriods(adjustment);
  const selectedPeriod = allPeriods.find((p) => p.id === selectedPeriodId) || allPeriods.find((p) => p.status === 'current') || allPeriods[0];
  const breaksUrl = `/api/prices/${selectedPeriod.id}/breaks`;
  const breaks = [...(selectedPeriod.breaks || [])].sort((a, b) => a.min_quantity - b.min_quantity);
  const isFixedOverride = !selectedPeriod.rule_id;
  // isFixedOverride reflects the last-saved state; the Price Breaks card
  // also needs to hide the instant the selected row's own InlineMethodSelect
  // is flipped to Calculation, before a Rule is even chosen — otherwise
  // Breaks sits there implying Fixed is still live for exactly the
  // in-between window the standalone Method card used to create.
  const showPriceBreaks = isFixedOverride
    && !(pendingMethodChoice.periodId === selectedPeriod.id && pendingMethodChoice.choice === 'rule');
  const isRetail = !!adjustment.customer_price_group?.is_system_default;
  // Mirrors priceController.js's assertMethodStillChangeable exactly, per
  // period: still changeable as long as that period has no Price Break yet
  // and no sibling period exists for this Scope (siblings lock every period
  // in the group together, the moment there's more than one). A Rule
  // reference gets this same window now, not an instant lock — see that
  // function's own comment.
  const isMethodLocked = (p) => (p.breaks?.length || 0) > 0 || allPeriods.length > 1;
  // The suggested next Start Date for "+ Schedule a new period" is the day
  // after the chronologically LAST period's own end, and only when that
  // last period is actually closed — checking "last period that happens to
  // have an end_date" instead (as this used to) is wrong whenever a later,
  // still-open period already exists: periods are contiguous, so the day
  // after an earlier closed period's end is exactly that later open
  // period's own start date, suggesting a guaranteed overlap. An
  // open-ended last period gets no suggestion at all — there's no
  // non-colliding date to offer; typing one just auto-supersedes/truncates
  // it on save, same as the list's own Add already does.
  const lastPeriod = allPeriods[allPeriods.length - 1];
  const suggestedNextFrom = lastPeriod?.end_date ? nextDayString(lastPeriod.end_date) : '';
  const customerScope = customerScopeRow(adjustment);
  const productRows = productScopeRows(adjustment);
  const orderQtyConstraint = adjustment.order_qty_constraint;
  const qtyMin = orderQtyConstraint ? orderQtyConstraint.min_order_qty : 1;
  const qtyStep = orderQtyConstraint ? orderQtyConstraint.qty_step : 1;
  const qtyMax = orderQtyConstraint?.max_order_qty ?? undefined;

  // A blank uom_id means "this Product's Base UoM" (same convention the
  // backend's resolveScope uses) — resolve it the same way here so the
  // helper still applies when the adjustment's own UoM was left blank but
  // happens to not be the base (which can't happen — kept for clarity).
  const effectiveUomId = adjustment.uom_id || adjustment.product?.base_uom_id;
  const effectiveProductUom = productUoms.find((u) => String(u.uom_id) === String(effectiveUomId));
  const baseProductUom = productUoms.find((u) => u.is_base);
  // Only offer the Base-Unit entry mode when this adjustment's own UoM is a
  // real non-base UoM (a Pack, Box...) with a known conversion rate — for a
  // Base-UoM-scoped adjustment there's nothing to convert.
  const showUomPriceHelper = !!(effectiveProductUom && !effectiveProductUom.is_base && baseProductUom);
  const conversionRate = effectiveProductUom ? effectiveProductUom.conversion_rate : null;
  const calculatedRetailUoms = productUoms
    .filter((u) => !u.is_base && u.can_sell)
    .sort((a, b) => Number(a.conversion_rate) - Number(b.conversion_rate));

  // Min/Max/Step Order Qty (qtyMin/qtyMax/qtyStep above) are defined in
  // this Price's own selling UoM, but a Price Break's min_quantity is now
  // stored in Base UoM (Pieces) — see priceBreakController.js's identical
  // conversion for the server-side check this mirrors for the input's own
  // min/max/step hints. Only meaningful once a conversion rate is known.
  const baseQtyMin = conversionRate ? qtyMin * conversionRate : qtyMin;
  const baseQtyMax = conversionRate && qtyMax !== undefined ? qtyMax * conversionRate : qtyMax;
  const baseQtyStep = conversionRate ? qtyStep * conversionRate : qtyStep;

  // Add a Price Break's helper text (below the Price field) always shows
  // the OPPOSITE of what's currently selected — typing Ex-GST shows what
  // it is Inc-GST, and vice versa — for both the same-UoM figure and the
  // cross-UoM one, rather than one of them just echoing the typed value
  // back under a mismatched label. Ex-normalized first (same conversion
  // handleAddBreak itself does at save time) so both helper lines and the
  // actual save always agree on what addPrice really means.
  // Retail's Base Price is always typed inc GST.
  const entryGstMode = isRetail ? 'inc' : addGstMode;
  // A non-Retail price is held ex GST to the cent, so one typed inc is
  // rounded to the cent ex here — shown in the helper before it's saved.
  // Retail's Base Price is held inc as typed (see handleAddBreak).
  const addExPrice = addPrice === '' ? null : (
    entryGstMode === 'inc' && defaultTaxRate !== null
      ? (isRetail ? Number(addPrice) / (1 + defaultTaxRate) : Math.round((Number(addPrice) / (1 + defaultTaxRate)) * 100) / 100)
      : Number(addPrice)
  );
  const addOppositeGstLabel = entryGstMode === 'ex' ? 'inc GST' : 'ex GST';
  const addOppositeSameUomPrice = addExPrice === null ? null : (
    entryGstMode === 'ex' ? addExPrice * (1 + defaultTaxRate) : addExPrice
  );
  const addOtherUomLabel = addPriceUomMode === 'base' ? effectiveProductUom?.name : baseProductUom?.name;
  const addOtherUomExPrice = addExPrice === null || !conversionRate ? null : (
    addPriceUomMode === 'base' ? addExPrice * conversionRate : addExPrice / conversionRate
  );
  const addOtherUomDisplayPrice = addOtherUomExPrice === null ? null : (
    defaultTaxRate !== null && entryGstMode === 'ex' ? addOtherUomExPrice * (1 + defaultTaxRate) : addOtherUomExPrice
  );

  // What actually gets saved as min_quantity (always Base UoM) — typing in
  // this Price's own UoM (the default, addQtyUomMode 'uom') converts up;
  // typing directly in Base terms needs no conversion. Mirrors
  // addOtherUomExPrice's own uom/base split for Price above.
  const addBaseQty = addQuantity === '' ? null : (
    addQtyUomMode === 'uom' && conversionRate ? Number(addQuantity) * conversionRate : Number(addQuantity)
  );
  const addQtyOtherValue = addQuantity === '' || !conversionRate ? null : (
    addQtyUomMode === 'uom'
      ? formatBaseQty(addQuantity, conversionRate, baseProductUom)
      : formatSellingQty(addQuantity, conversionRate, effectiveProductUom)
  );

  const handleScheduleNewPeriod = async (e) => {
    e.preventDefault();
    if (!newPeriodFrom) return showError(null, 'Effective From is required.');
    if (newPeriodMethod === 'rule' && !newPeriodRuleId) return showError(null, 'Select a Price Rule.');
    setSchedulingSaving(true);
    try {
      const methodFields = newPeriodMethod === 'rule'
        ? { rule_id: Number(newPeriodRuleId) }
        : { type: 'fixed_override' };
      const res = await axios.post('/api/prices', {
        product_id: adjustment.product_id,
        product_price_group_id: adjustment.product_price_group_id,
        customer_id: adjustment.customer_id,
        customer_price_group_id: adjustment.customer_price_group_id,
        uom_id: adjustment.uom_id,
        start_date: newPeriodFrom,
        end_date: newPeriodTo || null,
        ...methodFields,
      });
      dismissError();
      setShowScheduleForm(false);
      // No navigate — the new period shows up as a sibling in the same
      // Scope's schedule on the next fetch, and gets selected directly.
      setSelectedPeriodId(res.data.data.id);
      // Only meaningful for a Fixed Amount period — a no-op ref focus
      // otherwise, since a Rule-priced period never renders the Add-a-
      // Price-Break form this focuses.
      if (newPeriodMethod === 'fixed') setFocusMinQtyPeriodId(res.data.data.id);
      await fetchData();
    } catch (err) {
      showError(err, 'Could not add a Price Period');
    }
    setSchedulingSaving(false);
  };

  // Sets (or changes, while still unlocked) any period's pricing Method via
  // the same header-only PUT handleUpdatePeriodDate uses, just with
  // rule_id/type included this time — that's what tells editPrice's
  // changingPricingMethod check to actually touch Method instead of
  // leaving it alone. Takes the specific period being edited (the Schedule
  // table's InlineMethodSelect can be on any row, not just the selected
  // one — same as that table's own inline date edits already work per row).
  const handleSetMethod = async (period, choice, ruleId) => {
    setMethodSaving(true);
    try {
      const methodFields = choice === 'rule' ? { rule_id: Number(ruleId) } : { type: 'fixed_override' };
      await axios.put(`/api/prices/${period.id}`, {
        product_id: adjustment.product_id,
        product_price_group_id: adjustment.product_price_group_id,
        customer_id: adjustment.customer_id,
        customer_price_group_id: adjustment.customer_price_group_id,
        uom_id: adjustment.uom_id,
        start_date: period.start_date,
        end_date: period.end_date,
        ...methodFields,
      });
      dismissError();
      await fetchData();
    } catch (err) {
      showError(err, 'Could not update pricing Method');
    }
    setMethodSaving(false);
  };

  // Inline Start/End editing for any period in the Schedule table — this
  // one or a sibling. Every period sharing this Scope carries the identical
  // Scope/UoM fields by definition, so `adjustment`'s own header fields are
  // echoed back regardless of which period's own dates are being changed —
  // header-only, no rule_id/type sent, so this never touches pricing
  // Method. The server-side overlap guard (findOverlappingPrice, excludeId
  // set) already rejects a date that would collide with a sibling period.
  const handleUpdatePeriodDate = async (period, field, value) => {
    const nextValue = value || null;
    if (nextValue === (period[field] || null)) return;
    try {
      await axios.put(`/api/prices/${period.id}`, {
        product_id: adjustment.product_id,
        product_price_group_id: adjustment.product_price_group_id,
        customer_id: adjustment.customer_id,
        customer_price_group_id: adjustment.customer_price_group_id,
        uom_id: adjustment.uom_id,
        start_date: field === 'start_date' ? nextValue : period.start_date,
        end_date: field === 'end_date' ? nextValue : period.end_date,
      });
      await fetchData();
      dismissError();
    } catch (err) {
      showError(err, "Could not update this period's dates");
    }
  };

  const startChangeGroup = async () => {
    setNewGroupId('');
    setChangingGroup(true);
    try {
      const res = await axios.get('/api/customer-price-groups');
      setCustomerGroups((res.data.data || []).filter((g) => g.is_active && String(g.id) !== String(adjustment.customer_price_group_id)));
    } catch (err) {
      setCustomerGroups([]);
    }
  };

  const handleChangeGroup = async () => {
    const target = customerGroups.find((g) => String(g.id) === String(newGroupId));
    if (!target) return showError(null, 'Select a Customer Price Group.');
    const count = allPeriods.length;
    if (!(await askConfirm(`Move all ${count} price period${count === 1 ? '' : 's'} from ${adjustment.customer_price_group.name} to ${target.name}?`, { confirmLabel: 'Move' }))) return;
    setGroupSaving(true);
    try {
      await axios.put(`/api/prices/${id}/line/customer-group`, { customer_price_group_id: target.id });
      setChangingGroup(false);
      await fetchData();
      dismissError();
    } catch (err) {
      showError(err, 'Could not change the Customer Price Group');
    }
    setGroupSaving(false);
  };

  // Removes every period sharing this Scope in one action, not just the one
  // currently shown. Not gated on any period's own date status — a Price is
  // either inside its own Effective window or it's deleted, so there's
  // nothing to require clearing first. The confirm dialog is the safety
  // check instead: name the count, and call out if any period is Current.
  const handleDeleteLine = async () => {
    const currentCount = allPeriods.filter((p) => p.status === 'current').length;
    const currentWarning = currentCount > 0 ? ` ${currentCount} of them ${currentCount === 1 ? 'is' : 'are'} currently in effect.` : '';
    if (!(await askConfirm(`Permanently delete all ${allPeriods.length} price period${allPeriods.length === 1 ? '' : 's'} for this Scope?${currentWarning} This cannot be undone.`, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/prices/${id}/line`);
      dismissError();
      navigate('/sales-prices');
    } catch (err) {
      showError(err, 'Could not delete this Price line');
    }
  };

  // Per-row delete — the gap that prompted removing is_active entirely
  // (only a whole-line delete existed before), and now also how a wrong
  // pricing Method gets corrected on a period once it's locked in
  // (priceController.js's assertMethodStillChangeable): delete this period,
  // schedule a replacement. Deleting the period the route itself points to
  // (`adjustment.id`) still needs a navigate — that id would otherwise
  // 404 on the next fetch — but
  // deleting a sibling, or whichever period was just selected in-page,
  // only needs a refetch; the selection-resolution effect picks a new
  // default once the deleted one stops existing among the periods.
  const handleDeletePeriod = async (period) => {
    // Deleting the only period isn't "just this period" — there's no Price
    // left standing for this Scope afterward, same practical effect as
    // "Delete entire Schedule" above. A soft warning here beats a hard
    // block: still one click away if that's genuinely what's wanted, just
    // named honestly instead of the generic wording every other row gets.
    const confirmMessage = allPeriods.length === 1
      ? `This is the only period for this Sales Price — deleting it removes the Sales Price entirely (${formatDate(period.start_date) || '(any)'} – ${formatDate(period.end_date) || '(any)'}). This cannot be undone. Continue?`
      : `Permanently delete this price period (${formatDate(period.start_date) || '(any)'} – ${formatDate(period.end_date) || '(any)'})? This cannot be undone.`;
    if (!(await askConfirm(confirmMessage, { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`/api/prices/${period.id}`);
      dismissError();
      if (period.id === adjustment.id) {
        const remaining = allPeriods.filter((p) => p.id !== period.id);
        if (remaining.length === 0) {
          navigate('/sales-prices');
        } else {
          navigate(`/sales-prices/${remaining[0].id}`);
        }
        return;
      }
      if (period.id === selectedPeriodId) setSelectedPeriodId(null);
      await fetchData();
    } catch (err) {
      showError(err, 'Could not delete this price period');
    }
  };

  const handleAddBreak = async (e) => {
    e.preventDefault();
    if (!addQuantity || Number(addQuantity) <= 0) return showError(null, 'Minimum quantity must be a positive number.');
    if (!addPrice || Number(addPrice) <= 0) return showError(null, 'Price must be a positive number.');
    setAddSaving(true);
    try {
      // Retail's Base Price is keyed and held inc GST — sent as typed, the
      // server works out its ex figure (priceBreakController.js).
      if (isRetail) {
        await axios.post(breaksUrl, { min_quantity: Math.round(addBaseQty), price_inc: Number(addPrice) });
        setAddQuantity('');
        setAddPrice('');
        await fetchData();
        dismissError();
        setAddSaving(false);
        return;
      }
      const exPrice = addExPrice;
      // What was typed is a per-Base-unit price — convert up to this
      // adjustment's own UoM (e.g. Pack) before saving, since a Price Break
      // is always stored in terms of the adjustment's UoM, never Base.
      const uomPrice = addPriceUomMode === 'base' && conversionRate
        ? exPrice * conversionRate
        : exPrice;
      await axios.post(breaksUrl, { min_quantity: Math.round(addBaseQty), price: Number(uomPrice.toFixed(4)) });
      setAddQuantity('');
      setAddPrice('');
      minQtyInputRef.current?.focus();
      // Price is per (addPriceUomMode) deliberately isn't reset here — it's
      // a preference for the rest of this visit to the page, not a
      // per-break setting, so it should carry over to the next Add rather
      // than snapping back to Pack after every single break.
      await fetchData();
      dismissError();
    } catch (err) {
      showError(err, 'Could not add Price Break');
    }
    setAddSaving(false);
  };

  const handleUpdatePrice = async (brk, newValue) => {
    const val = Number(newValue);
    if (Number.isNaN(val) || val <= 0) return;
    // Re-typing the value already on the row (e.g. after a failed edit,
    // fixed by retyping the pre-existing valid number) is a no-op for the
    // API — but the stale error from that failed attempt still needs to
    // clear, or it sits there forever since nothing else will ever remove it.
    // Retail's Base Price is edited inc GST (see the breaks table below).
    const current = isRetail ? retailBreakInc(brk, defaultTaxRate) : Number(brk.price);
    if (val === current) return clearRowError(brk.id, 'price');
    try {
      await axios.patch(`${breaksUrl}/${brk.id}`, isRetail ? { price_inc: val } : { price: val });
      clearRowError(brk.id, 'price');
      await fetchData();
    } catch (err) {
      setRowError(brk.id, 'price', err.response?.data?.message || 'Could not update Price Break');
    }
  };

  const handleUpdateQuantity = async (brk, newValue) => {
    const val = Number(newValue);
    if (Number.isNaN(val) || val <= 0) return;
    // See handleUpdatePrice's identical guard above — same reasoning.
    if (val === Number(brk.min_quantity)) return clearRowError(brk.id, 'qty');
    try {
      await axios.patch(`${breaksUrl}/${brk.id}`, { min_quantity: val });
      clearRowError(brk.id, 'qty');
      await fetchData();
    } catch (err) {
      setRowError(brk.id, 'qty', err.response?.data?.message || 'Could not update Price Break');
    }
  };

  const handleDelete = async (brk) => {
    if (!(await askConfirm('Delete this Price Break?', { confirmLabel: 'Delete', destructive: true }))) return;
    try {
      await axios.delete(`${breaksUrl}/${brk.id}`);
      await fetchData();
      dismissError();
    } catch (err) {
      showError(err, 'Could not delete Price Break');
    }
  };

  return (
    <div className="price-pricing-page" style={{ width: '100%', maxWidth: 1000, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '20px 24px 56px' }}>
      <PageHeader
        crumbs={[
          { label: 'Sales Prices', onClick: () => navigate('/sales-prices') },
          { label: formatCustomerScope(adjustment) },
          { label: 'Pricing' },
        ]}
        title="Pricing"
        meta={(
          <div style={{ display: 'flex', flexDirection: 'column', rowGap: 6 }}>
            {[
              // Customer scope pairs with the product's first row when that
              // row is a Group (nothing else to pair it with) — but not when
              // it's a SKU, which reads better on its own line directly
              // under the Customer/Group line than crammed alongside it.
              // Any second product row (Product name) always gets its own
              // line too. UoM is last — no header-level Effective line since
              // the Schedule table below can hold several periods, each
              // with its own dates; a single date range up here would be
              // misleading.
              ...(productRows[0].label === 'SKU'
                ? [[customerScope], [productRows[0]]]
                : [[customerScope, productRows[0]]]),
              ...productRows.slice(1).map((row) => [row]),
              [uomScopeRow(adjustment)],
            ].map((line, i) => (
              <div key={i} style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                {line.map((row, j) => (
                  <div key={j}>
                    {row.label && <strong>{row.label}: </strong>}
                    {row === customerScope && changingGroup ? (
                      <>
                        <select className="styled-input" value={newGroupId} onChange={(e) => setNewGroupId(e.target.value)} style={{ width: 220 }}>
                          <option value="">Select a group…</option>
                          {customerGroups.map((g) => (
                            <option key={g.id} value={g.id}>{'   '.repeat(g.depth || 0)}{g.name}</option>
                          ))}
                        </select>
                        <SaveButton type="button" loading={groupSaving} disabled={!newGroupId} onClick={handleChangeGroup} style={{ marginLeft: 8 }}>Move</SaveButton>
                        <CancelButton onClick={() => setChangingGroup(false)} disabled={groupSaving} style={{ marginLeft: 8 }} />
                      </>
                    ) : row.value}
                    {row === customerScope && isRetail && <span style={{ marginLeft: 6 }}><SystemPill /></span>}
                    {row === customerScope && !changingGroup && adjustment.customer_price_group && (
                      <button
                        type="button"
                        onClick={startChangeGroup}
                        title="Moves every period in this Schedule to another Customer Price Group"
                        style={{ marginLeft: 10, background: 'none', border: 'none', padding: 0, color: '#1976d2', fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        Change
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      />

      {/* Page-level, not a Schedule-section action — this removes the whole
          Sales Price record (every period, all its Breaks), not something
          scoped to the Schedule table below. Kept visually separate from
          the day-to-day period actions (+ Schedule a new period, per-row
          Delete) so it doesn't read as one more of those. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button
          type="button"
          className="action-button"
          style={{ color: '#b91c1c', background: '#fef2f2', borderColor: '#fecaca' }}
          title="Deletes all dates and all Price Breaks"
          onClick={handleDeleteLine}
        >
          Delete Pricing
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>Price Schedule</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 10 }}>
          <thead className="list-head">
            <tr>
              <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600 }}>Effective Dates</th>
              <th style={{ padding: '10px 8px 10px 2px', textAlign: 'left', fontWeight: 600 }}>Method</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600 }}>Status</th>
              <th style={{ padding: '10px 8px' }}></th>
            </tr>
          </thead>
          <tbody>
            {allPeriods.map((p) => {
              const isSelected = p.id === selectedPeriod.id;
              return (
              <tr
                // Dates in the key so a row remounts when its dates change
                // server-side (e.g. auto-closed by a newer period) — the
                // DateInputs below are uncontrolled, and defaultValue is only
                // read on mount.
                key={`${p.id}-${p.start_date}-${p.end_date}`}
                className={isSelected ? '' : 'list-row'}
                style={{ borderBottom: '1px solid #eee', cursor: isSelected ? 'default' : 'pointer', background: isSelected ? '#f0f6ff' : 'transparent' }}
                onClick={isSelected ? undefined : () => setSelectedPeriodId(p.id)}
              >
                <td style={{ padding: '6px 8px', fontWeight: isSelected ? 700 : 400 }}>
                  <DateInput
                    className="styled-input"
                    defaultValue={p.start_date || ''}
                    onBlur={(e) => handleUpdatePeriodDate(p, 'start_date', e.target.value)}
                    style={{ width: 138 }}
                  />
                  {' – '}
                  <DateInput
                    className="styled-input"
                    defaultValue={p.end_date || ''}
                    onBlur={(e) => handleUpdatePeriodDate(p, 'end_date', e.target.value)}
                    style={{ width: 138 }}
                  />
                  {isSelected && <span style={{ marginLeft: 8, fontSize: 11, color: '#1976d2', fontWeight: 600 }}>SELECTED</span>}
                </td>
                <td style={{ padding: '6px 8px 6px 2px', fontSize: 12, color: '#5a6b85' }}>
                  {!isRetail && !isMethodLocked(p) ? (
                    <InlineMethodSelect
                      period={p}
                      priceRules={priceRules}
                      onSetMethod={handleSetMethod}
                      onChoiceChange={(periodId, choice) => setPendingMethodChoice({ periodId, choice })}
                      saving={methodSaving}
                    />
                  ) : (
                    p.rule_id ? 'Calculation' : 'Fixed Amount'
                  )}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                  <StatusPill item={p} />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="action-button"
                    style={{ color: '#b91c1c', background: '#fef2f2', borderColor: '#fecaca', padding: '2px 8px', fontSize: 12 }}
                    title="Deletes this price period"
                    onClick={() => handleDeletePeriod(p)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {showScheduleForm ? (
          <form onSubmit={handleScheduleNewPeriod} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={{ fontSize: 12, color: '#5a6b85' }}>Effective From</label><br />
              <DateInput ref={effectiveFromInputRef} className="styled-input" value={newPeriodFrom} onChange={(e) => setNewPeriodFrom(e.target.value)} style={{ width: 140 }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#5a6b85' }}>Effective To (optional)</label><br />
              <DateInput className="styled-input" value={newPeriodTo} onChange={(e) => setNewPeriodTo(e.target.value)} min={newPeriodFrom || undefined} style={{ width: 140 }} />
            </div>
            {/* Retail is always a directly-entered Fixed Override (vis_prices-
                and-pricelists.md) — assertNotRetailRule rejects a Rule outright,
                so this choice is never offered there. */}
            {!isRetail && (
              <div>
                <label style={{ fontSize: 12, color: '#5a6b85' }}>Method</label><br />
                <div style={{ display: 'flex', gap: 10, height: 30, alignItems: 'center' }}>
                  <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <input type="radio" checked={newPeriodMethod === 'fixed'} onChange={() => setNewPeriodMethod('fixed')} />
                    Fixed Amount
                  </label>
                  <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <input type="radio" checked={newPeriodMethod === 'rule'} onChange={() => setNewPeriodMethod('rule')} />
                    Calculation
                  </label>
                </div>
              </div>
            )}
            {!isRetail && newPeriodMethod === 'rule' && (
              <div>
                <label style={{ fontSize: 12, color: '#5a6b85' }}>Price Rule</label><br />
                <select className="styled-input" value={newPeriodRuleId} onChange={(e) => setNewPeriodRuleId(e.target.value)} style={{ width: 200 }}>
                  <option value="">Select a Price Rule…</option>
                  {priceRules.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            )}
            <button type="submit" className="primary-button" disabled={schedulingSaving}>{schedulingSaving ? 'Saving...' : 'Create period'}</button>
            <CancelButton onClick={() => setShowScheduleForm(false)} style={{ marginLeft: 8 }} />
          </form>
        ) : (
          <button
            type="button"
            className="action-button"
            onClick={() => { setNewPeriodFrom(suggestedNextFrom); setNewPeriodTo(''); setNewPeriodMethod('fixed'); setNewPeriodRuleId(''); setShowScheduleForm(true); }}
          >
            + Add a Price Period
          </button>
        )}
      </div>

      {!isFixedOverride && selectedPeriod.rule && (
        <CalculationCard
          rule={selectedPeriod.rule}
          showUomPriceHelper={showUomPriceHelper}
          conversionRate={conversionRate}
          sellingUom={effectiveProductUom}
        />
      )}

      {showPriceBreaks && (
        <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {isRetail ? 'Base Price' : 'Price Breaks'}
          </h4>
          {breaks.length === 0 && (
            <div style={{ background: '#fdf3e0', color: '#b45309', borderRadius: 8, padding: 12, fontSize: 14, marginBottom: 16, fontWeight: 600 }}>
              Needs price - Add at least one Price Break below
            </div>
          )}

          {breaks.length > 0 && (
            <div style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead className="list-head">
                  <tr>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>
                      Min Qty<br />(Base UoM)
                    </th>
                    {showUomPriceHelper && (
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#888' }}>
                        = {effectiveProductUom.name}
                      </th>
                    )}
                    {/* Retail's Base Price is keyed inc GST; every other
                        Group's prices are keyed ex GST. The keyed figure
                        is the editable column, the other is shown grey. */}
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>
                      {effectiveProductUom && <>{effectiveProductUom.name}<br /></>}
                      Price<TaxBasisLine>{isRetail ? 'inc GST' : 'ex GST'}</TaxBasisLine>
                    </th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#888' }}>
                      {effectiveProductUom && <>{effectiveProductUom.name}<br /></>}
                      Price<TaxBasisLine>{isRetail ? 'ex GST' : 'inc GST'}</TaxBasisLine>
                    </th>
                    {showUomPriceHelper && (
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#888' }}>
                        Equiv<br />{baseProductUom.name}<TaxBasisLine>ex GST</TaxBasisLine>
                      </th>
                    )}
                    {showUomPriceHelper && (
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#888' }}>
                        Equiv<br />{baseProductUom.name}<TaxBasisLine>inc GST</TaxBasisLine>
                      </th>
                    )}
                    <th style={{ padding: '10px 12px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {breaks.map((brk) => (
                    <tr key={brk.id} className="list-row">
                      <td style={{ padding: '6px 12px', textAlign: 'right', position: 'relative' }}>
                        <NumberInput
                          step={baseQtyStep}
                          min={baseQtyMin}
                          max={baseQtyMax}
                          maxDecimals={4}
                          defaultValue={Number(brk.min_quantity)}
                          onFocus={() => clearRowError(brk.id, 'qty')}
                          onBlur={(e) => handleUpdateQuantity(brk, e.target.value)}
                          style={{ width: 80, textAlign: 'right' }}
                        />
                        {rowErrors[brk.id]?.qty && (
                          <RowErrorCallout>{rowErrors[brk.id].qty}</RowErrorCallout>
                        )}
                      </td>
                      {showUomPriceHelper && (
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: '#888' }}>
                          {formatSellingQty(brk.min_quantity, conversionRate, effectiveProductUom)}
                        </td>
                      )}
                      <td style={{ padding: '6px 12px', textAlign: 'right', position: 'relative' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <InlineMoneyEdit price={isRetail ? retailBreakInc(brk, defaultTaxRate) : brk.price} onCommit={(val) => handleUpdatePrice(brk, val)} onFocus={() => clearRowError(brk.id, 'price')} />
                          <PriceCalculator initialValue={isRetail ? retailBreakInc(brk, defaultTaxRate) : brk.price} onResult={(val) => handleUpdatePrice(brk, val)} />
                        </div>
                        {rowErrors[brk.id]?.price && (
                          <RowErrorCallout>{rowErrors[brk.id].price}</RowErrorCallout>
                        )}
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', color: '#888' }}>
                        {isRetail ? formatMoney3(brk.price) : (defaultTaxRate === null ? '—' : formatMoney3(Number(brk.price) * (1 + defaultTaxRate)))}
                      </td>
                      {showUomPriceHelper && (
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: '#888' }}>
                          {formatMoney3(Number(brk.price) / conversionRate)}
                        </td>
                      )}
                      {showUomPriceHelper && (
                        <td style={{ padding: '6px 12px', textAlign: 'right', color: '#888' }}>
                          {defaultTaxRate === null ? '—' : formatMoney3((Number(brk.price) / conversionRate) * (1 + defaultTaxRate))}
                        </td>
                      )}
                      <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                        <button type="button" className="action-button" style={{ color: '#b91c1c', background: '#fef2f2', borderColor: '#fecaca' }} onClick={() => handleDelete(brk)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ borderTop: '1px solid #eee', margin: '4px 0 16px' }} />

          {/* Retail's Price is one figure, the Base Price — no quantity
              breaks (priceBreakController.js rejects a second one). */}
          {isRetail && breaks.length > 0 ? null : showAddBreakForm ? (
            // paddingBottom reserves room for the Min Qty/Price helper text
            // below — those are position:absolute (so their variable height
            // doesn't shift the row's own field alignment) and so don't
            // otherwise count toward this form's height, which left them
            // overlapping whatever came after the Price Breaks card.
            <form onSubmit={handleAddBreak} style={{ paddingBottom: 34 }}>
              <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
              {showUomPriceHelper && (
                <div>
                  <label style={{ fontSize: 12, color: '#5a6b85' }}>Qty is in</label><br />
                  <div style={{ display: 'flex', gap: 10, height: 30, alignItems: 'center' }}>
                    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <input type="radio" checked={addQtyUomMode === 'base'} onChange={() => setAddQtyUomMode('base')} />
                      {baseProductUom.name} (Base)
                    </label>
                    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <input type="radio" checked={addQtyUomMode === 'uom'} onChange={() => setAddQtyUomMode('uom')} />
                      {effectiveProductUom.name}
                    </label>
                  </div>
                </div>
              )}
              {showUomPriceHelper && (
                <div>
                  <label style={{ fontSize: 12, color: '#5a6b85' }}>Price is per</label><br />
                  <div style={{ display: 'flex', gap: 10, height: 30, alignItems: 'center' }}>
                    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <input type="radio" checked={addPriceUomMode === 'base'} onChange={() => setAddPriceUomMode('base')} />
                      {baseProductUom.name} (Base)
                    </label>
                    <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <input type="radio" checked={addPriceUomMode === 'uom'} onChange={() => setAddPriceUomMode('uom')} />
                      {effectiveProductUom.name}
                    </label>
                  </div>
                </div>
              )}
              {!isRetail && <div>
                <label style={{ fontSize: 12, color: '#5a6b85' }}>Enter as</label><br />
                <div
                  style={{ display: 'flex', gap: 10, height: 30, alignItems: 'center' }}
                  title={defaultTaxRate === null ? 'No default Tax Code configured — Inc-GST entry unavailable' : undefined}
                >
                  <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <input type="radio" checked={addGstMode === 'ex'} onChange={() => setAddGstMode('ex')} />
                    Ex-GST
                  </label>
                  <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 3, opacity: defaultTaxRate === null ? 0.5 : 1 }}>
                    <input type="radio" checked={addGstMode === 'inc'} disabled={defaultTaxRate === null} onChange={() => setAddGstMode('inc')} />
                    Inc-GST
                  </label>
                </div>
              </div>}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <label style={{ fontSize: 12, color: '#5a6b85' }}>
                  Min Qty {showUomPriceHelper
                    ? `(${addQtyUomMode === 'uom' ? effectiveProductUom.name : `${baseProductUom.name} (Base)`})`
                    : '(Base UoM)'}
                </label><br />
                <NumberInput
                  ref={minQtyInputRef}
                  maxDecimals={4}
                  step={addQtyUomMode === 'base' ? baseQtyStep : qtyStep}
                  min={addQtyUomMode === 'base' ? baseQtyMin : qtyMin}
                  max={addQtyUomMode === 'base' ? baseQtyMax : qtyMax}
                  value={addQuantity}
                  onChange={(e) => setAddQuantity(e.target.value)}
                  style={{ width: 90 }}
                />
                {showUomPriceHelper && addQuantity !== '' && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, whiteSpace: 'nowrap', marginTop: 2, fontSize: 11, color: '#888' }}>
                    = {addQtyOtherValue}
                  </div>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <label style={{ fontSize: 12, color: '#5a6b85' }}>Price</label><br />
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <NumberInput min="0" minDecimals={2} maxDecimals={4} value={addPrice} onChange={(e) => setAddPrice(e.target.value)} style={{ width: 100 }} />
                  <PriceCalculator initialValue={addPrice === '' ? null : Number(addPrice)} onResult={(val) => setAddPrice(String(val))} />
                </div>
                {addPrice !== '' && (defaultTaxRate !== null || showUomPriceHelper) && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, whiteSpace: 'nowrap', marginTop: 2 }}>
                    {defaultTaxRate !== null && (
                      <div style={{ fontSize: 11, color: '#888' }}>
                        {addOppositeGstLabel}: {formatMoney4(addOppositeSameUomPrice)}
                      </div>
                    )}
                    {showUomPriceHelper && (
                      <div style={{ fontSize: 11, color: '#888' }}>
                        = {formatMoney4(addOtherUomDisplayPrice)} per {addOtherUomLabel}
                        {defaultTaxRate !== null && ` (${addOppositeGstLabel})`}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button type="submit" className="primary-button" disabled={addSaving}>{addSaving ? 'Saving...' : '+ Add break'}</button>
              <CancelButton onClick={() => setShowAddBreakForm(false)} style={{ marginLeft: 8 }} />
              </div>
            </form>
          ) : (
            <button type="button" className="action-button" onClick={() => { pendingAddBreakFocus.current = true; setShowAddBreakForm(true); }}>
              + Add a Price Break
            </button>
          )}
        </div>
      )}

      {/* Every other UoM's Retail Price is calculated from this Base Price
          (retailPriceService.js) — shown here, read-only, so the Pack/Box
          figures a Pricelist, quote or order will use are visible where the
          Base Price is set. */}
      {isRetail && effectiveProductUom?.is_base && calculatedRetailUoms.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Calculated {adjustment.customer_price_group?.name} Prices
          </h4>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            Base Price × quantity for each UoM sold. Change the Base Price to change these.
          </div>
          <div style={{ borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead className="list-head">
                <tr>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>UoM</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Qty<br />({baseProductUom?.name})</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Price<TaxBasisLine>inc GST</TaxBasisLine></th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#888' }}>Price<TaxBasisLine>ex GST</TaxBasisLine></th>
                </tr>
              </thead>
              <tbody>
                {calculatedRetailUoms.map((u) => {
                  const price = calculatedUomRetailPrice(retailBreakInc(breaks[0], defaultTaxRate), u.conversion_rate, defaultTaxRate);
                  return (
                    <tr key={u.uom_id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '6px 12px' }}>{u.name}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right' }}>{formatNumber(u.conversion_rate, { maxDecimals: 4 })}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right' }}>{price === null ? '—' : formatMoney2(price.inc)}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', color: '#888' }}>{price === null ? '—' : formatMoney4(price.ex)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <BrowseNav id={id} ids={browseIds} buildPath={(targetId) => `/sales-prices/${targetId}`} noun="Prices" />
    </div>
  );
}

export default PricePricingPage;
